import { Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, tap } from 'rxjs/operators';

import { AnalysisStateStore } from '../../stores/analysis-state';
import { AnalystStore } from '../../stores/analyst';
import { AssetStore } from '../../stores/asset';
import { AnalysisViewContext, SampleLocation, ViewContextStore, viewGeneration } from '../../stores/view-context';
import { MosaicGalleryPanels } from '../../stores/mosaic-gallery-panels';
import { RoiChangeStore } from '../../stores/roi-change';
import { MosaicPaginationStore } from '../../stores/mosaic-pagination';
import { RoiStore } from '../../stores/roi';
import { RoiRestoreEffects } from '../roi-restore';
import { RoiStatsEffects, SampleStatsApplied } from '../roi-stats';
import { ProtocolStore } from '@telespot/protocols/ui';

/**
 * Owns the per-sample reads of an analysis view, and the generation they are scoped by.
 *
 * The view tells it WHERE it is (`openLocation`); it works out WHAT is on screen — the
 * location plus the analyst actually resolved for it — publishes that as the view context,
 * and drives every per-sample read from it. Nothing here samples "current" state at the
 * moment a trigger happens to fire, which is what let a read be paired with one generation
 * and applied to another.
 *
 * Stateless by design: the only state is the context, and it lives in `ViewContextStore`,
 * which this class is the sole writer of.
 *
 * Not every read is per-analyst. Assets and analysis states are keyed on the LOCATION, so
 * they start as soon as the route settles rather than waiting for an analyst round-trip;
 * only reads that depend on whose analysis is shown wait for the settled context.
 */
@Injectable()
export class SampleViewLoadingEffects {
  constructor(
    private readonly viewContext: ViewContextStore,
    private readonly analystStore: AnalystStore,
    private readonly assetStore: AssetStore,
    private readonly analysisStateStore: AnalysisStateStore,
    private readonly roiStatsEffects: RoiStatsEffects,
    private readonly roiRestoreEffects: RoiRestoreEffects,
    private readonly roiStore: RoiStore,
    private readonly roiChangeStore: RoiChangeStore,
    private readonly panelRegistry: MosaicGalleryPanels,
    private readonly protocolStore: ProtocolStore,
    private readonly paginationStore: MosaicPaginationStore
  ) {
    this.wireContext();
    this.wireLocationScopedReads();
    this.wireGenerationScopedReads();
    this.wireAfterStatsReads();
  }

  /**
   * Where the view is. Call it on every route change: the context producer decides what
   * is a new generation, so repeated calls for the same location are free.
   */
  public readonly openLocation = this.viewContext.effect((location$: Observable<SampleLocation>) =>
    location$.pipe(
      filter(({ caseId, sampleId }) => !!caseId && !!sampleId),
      tap<SampleLocation>((location) => this.viewContext.setLocation(location))
    )
  );

  /**
   * Produces the settled context from the location and the analyst resolved FOR it.
   *
   * "Settled" is a causal condition, not a timing one: the store records which sample it
   * last resolved for, and the context is published only when that matches the location.
   *
   * A timing condition was the obvious alternative and it is wrong. Waiting for
   * `AnalystStore.loading` to clear only works if the resolution has already been
   * triggered — and the route notifies subscribers in subscription order, so whichever of
   * "set the location" and "resolve the analyst" happens to be wired first decides the
   * answer. Wired the way the facade is, `loading` is still false when the location
   * arrives, so the context would pair the NEW sample with the OUTGOING analyst: exactly
   * the class of defect this whole coordinator exists to remove.
   *
   * The analyst is also a trigger in its own right — picking a different analyst on the
   * same sample is a new generation, and those paths (`getCurrentAnalyst`, `getHistory`,
   * `selectAnalyst`) leave `resolvedSampleId` alone, so it still matches and the new
   * generation publishes immediately.
   *
   * The protocol is the third thing a generation's reads need. `getSampleStats` seeds the
   * label index and the pagination from `ProtocolStore.options$`, which returns an EMPTY
   * record until the method type, categories, steps and labels have all arrived — and a
   * read that lands first seeds an empty grid, leaving `nextLabel$` undefined so no rois
   * are ever fetched. Since the grid is only re-seeded for a new generation, the protocol
   * arriving afterwards does not repair it.
   *
   * Gating on `ProtocolStore.loading$` IS sound here, even though the analyst's equivalent
   * flag was not: all four protocol stores initialise `loading: true` and re-raise it at
   * the start of every `findForSample`, so it cannot read false before the work begins.
   * They also clear it on error, so a protocol that fails to load cannot stall the view.
   */
  private wireContext(): void {
    this.viewContext.effect<void>(() =>
      combineLatest([
        this.viewContext.location$,
        this.analystStore.selectedAnalyst$,
        this.analystStore.resolvedSampleId$,
        this.protocolStore.loading$,
      ]).pipe(
        filter(
          ([location, analyst, resolvedSampleId, protocolLoading]) =>
            !!analyst && resolvedSampleId === location.sampleId && !protocolLoading
        ),
        map(
          ([location, analyst]): AnalysisViewContext => ({
            caseId: location.caseId,
            sampleId: location.sampleId,
            analyst,
            generation: viewGeneration(location.sampleId, analyst.id),
          })
        ),
        distinctUntilChanged((a, b) => a.generation === b.generation),
        tap<AnalysisViewContext>((context) => this.viewContext.setContext(context))
      )
    );
  }

  /**
   * Reads scoped to a generation: they depend on WHOSE analysis is shown, so they wait for
   * the settled context and re-run when either half of it changes.
   */
  private wireGenerationScopedReads(): void {
    this.viewContext.effect<void>(() =>
      this.viewContext.context$.pipe(
        tap<AnalysisViewContext>(({ sampleId, analyst }) => {
          this.beginGeneration();
          this.roiStatsEffects.getSampleStats({ sampleId, analyst });
        })
      )
    );
  }

  /**
   * Discards everything derived from the generation being left, as ONE ordered step with
   * the read that replaces it. Previously the discard was its own subscription on the
   * sample id, so "clear before read" held only because of where it sat in the facade's
   * constructor — and it covered a sample change but not an analyst switch, which relied
   * on the stats read clearing the grid as a side effect instead.
   *
   * `resetForRestoration` is what keeps the view's combined loading flag raised across the
   * whole transition. `clearAllChanges` alone leaves `changeRestorationComplete` true, so
   * between the clear and the restore the mosaic reads as "loaded, no unsaved changes" —
   * briefly showing a synced state that is not true. Nothing is lost by clearing: unsaved
   * changes are persisted and re-read per (sample, analyst).
   */
  private beginGeneration(): void {
    // ORDER MATTERS, and not for taste: `clearRois` patches RoiStore, whose selectors are
    // synchronous, so the gallery's paging trigger (sectionCellCounts$ -> shouldLoadMore,
    // which is true for an empty index) re-enters `loadMore` DURING this method. Whatever
    // has not been cleared by that point is read as if it belonged to the new generation.
    //
    // With the rois cleared first, that re-entrant read saw the previous generation's
    // pagination — new creator, old start key — which is what the backend rejects. The
    // stats-loading gate cannot help: `roiStatsStore.loading$` is debounced, and the read
    // that raises it has not even been issued yet.
    //
    // So everything else goes first, and the rois — the trigger — go last.
    //
    // Pagination tokens belong to the generation that earned them: a DynamoDB start key is
    // only valid for the sample and creator it was issued under.
    this.paginationStore.clearProgress();
    this.roiChangeStore.clearAllChanges();
    this.roiChangeStore.resetForRestoration();
    this.panelRegistry.clearAllPanelSelections();
    this.roiStore.clearRois();
  }

  /**
   * Restores the persisted unsaved changes of the generation whose counters just landed,
   * and measures whether the async projection has caught up with it.
   *
   * Both are keyed on what `statsApplied$` reports rather than on the current selection: a
   * read that settled after the selection moved on would otherwise restore one analyst's
   * changes onto another's view.
   */
  private wireAfterStatsReads(): void {
    this.viewContext.effect<void>(() =>
      this.roiStatsEffects.statsApplied$.pipe(
        concatLatestFrom(() => this.viewContext.context$),
        filter(
          ([applied, context]) => viewGeneration(applied.sampleId, applied.analystId) === context.generation
        ),
        tap<[SampleStatsApplied, AnalysisViewContext]>(([applied, context]) => {
          if (applied.isNewGeneration) {
            this.roiRestoreEffects.restoreUnsavedChanges({
              sampleId: applied.sampleId,
              analystId: applied.analystId,
            });
          }
          this.roiStatsEffects.checkProjection({ sampleId: context.sampleId, analyst: context.analyst });
        })
      )
    );
  }

  /**
   * Re-read the counters for the generation on screen WITHOUT starting it over: an AI poll
   * tick, or a re-read after a save. `getSampleStats` distinguishes the two, leaving the
   * loaded rows and pagination alone for a refresh.
   */
  public readonly refreshStats = this.viewContext.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.viewContext.context$),
      tap(([, { sampleId, analyst }]) => this.roiStatsEffects.getSampleStats({ sampleId, analyst }))
    )
  );

  /**
   * Reads that do not depend on the analyst. Keyed on the location so the reference strip
   * and the analyst hints are not held back by the analyst resolution.
   */
  private wireLocationScopedReads(): void {
    this.viewContext.effect<void>(() =>
      this.viewContext.location$.pipe(
        tap(({ caseId, sampleId }) => {
          this.assetStore.loadSampleAssets(sampleId);
          this.analysisStateStore.fetchAnalysisStates({ sampleId, caseId });
        })
      )
    );
  }

  /**
   * Re-read the analysis states of the sample on screen, for a live refresh that is not a
   * generation change (an AI poll tick).
   */
  public readonly refreshAnalysisStates = this.viewContext.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      concatLatestFrom(() => this.viewContext.context$),
      tap(([, { caseId, sampleId }]) => this.analysisStateStore.fetchAnalysisStates({ sampleId, caseId }))
    )
  );
}
